import { Form, Formik } from 'formik';
import { useState } from 'react';

import { useModal } from '../../hooks/modals';
import { Question, SurveyWave } from '../../types/domainModels';
import { ReactSelectValue } from '../../types/forms';
import { ExportFormData, getInitialExportFormData } from '../../util/exports';
import { getQuestionOptions } from '../../util/formOptions';
import { QUESTION_TYPE_DISPLAY_NAMES } from '../../util/questions';
import { stripHtml } from '../../util/richText';

import Badge from '../common/Badge';
import Icon from '../common/Icon';
import IconBackground from '../common/icons/IconBackground';
import AICodingConfigModal from './AICodingConfigModal';

type AICodingTab = 'configuration';

interface AICodingBuilderProps {
  questions: Question[];
  waves: SurveyWave[];
}

const AICodingBuilder = ({ questions, waves }: AICodingBuilderProps) => {
  const [activeTab] = useState<AICodingTab>('configuration');

  const questionsOptions = getQuestionOptions({
    includeInactiveQuestions: true,
    questions: questions.filter((q) => !q.isDemographic),
  });

  return (
    <Formik<ExportFormData>
      enableReinitialize
      initialValues={getInitialExportFormData({ questions: questionsOptions })}
      onSubmit={() => {}}
      validateOnChange={false}
    >
      <Form>
        <div className="pt-6 sticky top-0 bg-white z-10" />

        {activeTab === 'configuration' && (
          <ConfigurationTab questions={questionsOptions} waves={waves} />
        )}
      </Form>
    </Formik>
  );
};

export default AICodingBuilder;

const ConfigurationTab = ({
  questions,
  waves,
}: {
  questions: ReactSelectValue<Question>[];
  waves: SurveyWave[];
}) => {
  return (
    <div>
      <div className="mb-2">
        <div
          className="grid gap-4 px-2"
          style={{ gridTemplateColumns: '1fr 150px 40px' }}
        >
          <div className="flex items-center text-sm font-medium text-gray-d-600">
            Questions
          </div>
          <div className="flex items-center text-sm font-medium text-gray-d-600">
            Question Type
          </div>
          <div className="w-[40px]" />
        </div>
      </div>
      <div>
        {questions.map(({ value }) => {
          return (
            <div
              key={value.id}
              className="border-t border-light-grey last:border-b even:bg-gray-d-50"
            >
              <QuestionConfigRow question={value} waves={waves} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const QuestionConfigRow = ({
  question,
  waves,
}: {
  question: Question;
  waves: SurveyWave[];
}): JSX.Element => {
  const { isOpen, onCloseModal, setIsOpen } = useModal();

  const { isDemographic, sort, title, questionTypeId } = question;

  return (
    <>
      <div
        className="grid items-center gap-4 p-2 text-sm"
        style={{ gridTemplateColumns: '1fr 150px 40px' }}
      >
        <div className="pr-8 flex gap-4 items-center">
          <div className="font-medium text-gray-d-900 ml-1">
            {isDemographic ? 'D' : 'Q'}
            {sort}) {stripHtml(title)}
          </div>
          {!question.isActive && (
            <Badge color="gray" size="sm">
              Inactive
            </Badge>
          )}
        </div>

        <div className="text-sm text-gray-d-600">
          {QUESTION_TYPE_DISPLAY_NAMES[
            questionTypeId as keyof typeof QUESTION_TYPE_DISPLAY_NAMES
          ] || 'Unknown'}
        </div>

        <div className="flex items-center justify-center">
          <IconBackground
            onClick={() => {
              setIsOpen(true);
            }}
            title="Configure"
          >
            <div className="w-4 h-4">
              <Icon id="settings-01" />
            </div>
          </IconBackground>
        </div>
      </div>

      {isOpen && (
        <AICodingConfigModal
          onCloseModal={onCloseModal}
          question={question}
          waves={waves}
        />
      )}
    </>
  );
};
